/**
 * A rigid hull floating on the sea.
 *
 * Six degrees of freedom, but not all of them are treated equally. Surge, sway
 * and yaw are integrated from forces, because that is what the player is
 * steering. Heave, pitch and roll come from buoyancy sampled at points around
 * the hull — the hull is pushed up wherever it is under water, which produces
 * restoring torques for free and makes the boat ride waves rather than
 * intersect them.
 *
 * Body frame: +Z is forward (bow), +X is starboard, +Y is up. Yaw is the
 * standard right-handed rotation about +Y, so increasing yaw swings the bow to
 * starboard. Euler order is YXZ, which makes rotation.x pitch (positive = bow
 * down) and rotation.z roll (positive = starboard up).
 */

import { Euler, Quaternion, Vector3 } from 'three'
import { GRAVITY, sampleWater } from '../world/waves.ts'
import type { Obstacle } from '../world/Obstacle.ts'
import { closestPointOnObstacle } from '../world/Obstacle.ts'

/**
 * Fraction of the draft that sits below the waterline at rest. The hull origin
 * is placed exactly at the resting waterline, so buoyancy at this submersion
 * balances weight precisely and the boat spawns without a bounce.
 */
const REST_SUBMERSION = 0.5

export interface VesselConfig {
  name: string
  mass: number
  length: number
  beam: number
  draft: number
  /** Horizontal speed guard in m/s. Defaults to 40 for unpowered/traffic hulls. */
  maxSpeed?: number
  /** Longitudinal drag, in newtons per (m/s)^2. */
  dragForward: number
  /** Lateral drag. Much larger than longitudinal — that is why a hull tracks. */
  dragLateral: number
  /** Yaw drag, in newton-metres per (rad/s)^2. */
  dragYaw: number
  /**
   * How far aft of the centre of mass the lateral drag acts. A positive value
   * puts the centre of lateral resistance behind the pivot, which is what makes
   * the hull weathervane into its own sideslip instead of crabbing forever.
   */
  lateralResistanceOffset: number
  /** Rows x columns of buoyancy probes spread over the hull footprint. */
  buoyancySamples?: [rows: number, cols: number]
  pitchDamping?: number
  rollDamping?: number
  /**
   * Extra multiplier on yaw rotational inertia, on top of the base added-mass
   * factor. Above 1 makes the hull slower to start and stop turning — steadier
   * under tow and gentler on the helm. Defaults to 1.
   */
  yawInertiaScale?: number
  /**
   * Peak Coulomb friction, in newtons, when hard aground (only used if a
   * `bedSampler` is attached). A log wants this high enough to stay put until
   * the tug leans on the line; a boat wants it modest so it merely stops at
   * the shore rather than climbing the sand. Defaults to 0.
   */
  groundFriction?: number
}

export interface CollisionEvent {
  /** Closing speed along the contact normal, m/s. */
  impactSpeed: number
  point: Vector3
}

const _euler = new Euler(0, 0, 0, 'YXZ')
const _v = new Vector3()
const _worldPoint = new Vector3()
const _contact = new Vector3()

export class Vessel {
  readonly config: VesselConfig

  readonly position = new Vector3()
  readonly velocity = new Vector3()
  yaw = 0
  yawRate = 0
  pitch = 0
  pitchRate = 0
  roll = 0
  rollRate = 0

  /** Orientation as a quaternion, refreshed once per step. */
  readonly quaternion = new Quaternion()

  /** Fraction of buoyancy probes currently under water, 0..1. */
  submersion = 0

  /** Accumulated wear from collisions, 0..1. Reset by the game when scored. */
  damage = 0

  /** Collisions resolved during the last step. Cleared at the start of each. */
  readonly collisions: CollisionEvent[] = []

  /**
   * Seabed height at a world XZ, or null for bottomless deep water. When set,
   * the hull rests on the bed in the shallows instead of sinking through it,
   * and (with a non-zero `groundFriction`) grips the bottom — the whole basis
   * of dragging a beached log back into the water. Harbour mode leaves this
   * null, so nothing there changes.
   */
  bedSampler: ((x: number, z: number) => number) | null = null

  /** How aground the hull was last step, 0 (afloat) .. 1 (hard aground). */
  grounding = 0

  private readonly force = new Vector3()
  private torqueYaw = 0
  private readonly samples: { x: number; z: number }[]
  private readonly samplePitchMoment: number
  private readonly sampleRollMoment: number
  private readonly inertiaYaw: number
  private readonly inertiaPitch: number
  private readonly inertiaRoll: number
  private readonly naturalFrequency: number
  /** Circles along the centreline used for hull-vs-quay contact. */
  private readonly hullCircles: { z: number; r: number }[]

  constructor(config: VesselConfig) {
    this.config = config

    const [rows, cols] = config.buoyancySamples ?? [4, 3]
    this.samples = []
    for (let i = 0; i < rows; i++) {
      // Spread probes across the middle 90% of the hull; the very tips of the
      // bow and stern carry little buoyancy anyway.
      const tz = rows === 1 ? 0 : (i / (rows - 1)) * 2 - 1
      for (let j = 0; j < cols; j++) {
        const tx = cols === 1 ? 0 : (j / (cols - 1)) * 2 - 1
        this.samples.push({ x: tx * config.beam * 0.45, z: tz * config.length * 0.45 })
      }
    }

    this.samplePitchMoment = this.samples.reduce((sum, point) => sum + point.z * point.z, 0)
    this.sampleRollMoment = this.samples.reduce((sum, point) => sum + point.x * point.x, 0)

    // Added mass roughly doubles a hull's apparent rotational inertia.
    this.inertiaYaw = ((config.mass * config.length * config.length) / 12) * 1.6 * (config.yawInertiaScale ?? 1)
    this.inertiaPitch = ((config.mass * config.length * config.length) / 12) * 1.6
    this.inertiaRoll = ((config.mass * config.beam * config.beam) / 12) * 1.6
    this.naturalFrequency = Math.sqrt(GRAVITY / (config.draft * REST_SUBMERSION))

    // Enough circles that neighbours overlap — a long, lean hull otherwise has
    // gaps between circles that a quay corner can slide through unnoticed.
    const n = Math.max(5, Math.ceil(config.length / config.beam) + 2)
    this.hullCircles = []
    for (let i = 0; i < n; i++) {
      const t = (i / (n - 1)) * 2 - 1
      this.hullCircles.push({ z: t * (config.length / 2 - config.beam / 2), r: config.beam / 2 })
    }

    this.updateQuaternion()
  }

  // -- frame helpers --------------------------------------------------------

  /** Unit vector along the bow, in world space. */
  forward(target = _v): Vector3 {
    return target.set(Math.sin(this.yaw), 0, Math.cos(this.yaw))
  }

  /** Unit vector to starboard, in world space. */
  starboard(target = _v): Vector3 {
    return target.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw))
  }

  /** Speed along the bow, signed — negative means making sternway. */
  get forwardSpeed(): number {
    return this.velocity.x * Math.sin(this.yaw) + this.velocity.z * Math.cos(this.yaw)
  }

  get speed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z)
  }

  /** Convert a body-frame point to world space, including pitch and roll. */
  localToWorld(local: Vector3, target: Vector3): Vector3 {
    return target.copy(local).applyQuaternion(this.quaternion).add(this.position)
  }

  /** Rotate a body-frame direction into world space. Ignores translation. */
  localToWorldDirection(local: Vector3, target: Vector3): Vector3 {
    return target.copy(local).applyQuaternion(this.quaternion)
  }

  private updateQuaternion(): void {
    _euler.set(this.pitch, this.yaw, this.roll)
    this.quaternion.setFromEuler(_euler)
  }

  // -- force application ----------------------------------------------------

  /**
   * Apply a world-space force at a body-space point. The point's lever arm about
   * the vertical axis becomes yaw torque, which is how a tow line hooked to the
   * stern is able to swing the bow around.
   */
  applyForceAtPoint(worldForce: Vector3, bodyPoint: Vector3 | null): void {
    this.force.add(worldForce)
    if (!bodyPoint) return

    // Rotate the force into the body frame to take the moment about +Y.
    const cos = Math.cos(this.yaw)
    const sin = Math.sin(this.yaw)
    const fx = worldForce.x * cos - worldForce.z * sin
    const fz = worldForce.x * sin + worldForce.z * cos
    this.torqueYaw += bodyPoint.z * fx - bodyPoint.x * fz
  }

  /** Apply a force expressed in the body frame at a body-space point. */
  applyBodyForce(bodyForce: Vector3, bodyPoint: Vector3 | null): void {
    const cos = Math.cos(this.yaw)
    const sin = Math.sin(this.yaw)
    _v.set(bodyForce.x * cos + bodyForce.z * sin, bodyForce.y, -bodyForce.x * sin + bodyForce.z * cos)
    this.force.add(_v)
    if (bodyPoint) this.torqueYaw += bodyPoint.z * bodyForce.x - bodyPoint.x * bodyForce.z
  }

  applyYawTorque(torque: number): void {
    this.torqueYaw += torque
  }

  // -- integration ----------------------------------------------------------

  /**
   * Hook for subclasses to add propulsion before the shared solver runs.
   * Called once per fixed step with forces already zeroed.
   */
  protected applyControlForces(_dt: number): void {}

  step(dt: number, elapsed: number): void {
    this.collisions.length = 0

    // External forces (tow lines, future wind or wash) are applied *between*
    // steps and are already sitting in the accumulator here. Everything the
    // vessel does to itself is added on top, and the accumulator is cleared at
    // the end of the step, after integration — never at the start, where it
    // would silently discard the externals.
    this.applyControlForces(dt)
    this.applyGrounding()
    this.applyHydrodynamicDrag()
    this.applyBuoyancy(dt, elapsed)
    this.integrate(dt)

    this.force.set(0, 0, 0)
    this.torqueYaw = 0
  }

  /**
   * Rest on and grip the seabed where the water is too shallow to float in.
   *
   * Runs after control and tow forces are in, so its Coulomb friction can
   * oppose the *applied* force — proper stiction, not just velocity damping.
   * Below breakout the hull is pinned; lean on it harder and it slides with a
   * slightly lower kinetic friction, and as the bed falls away seaward the
   * grip fades to nothing and the hull floats off. No bed sampler means deep
   * water everywhere and this whole method is a no-op.
   */
  private applyGrounding(): void {
    if (!this.bedSampler) {
      this.grounding = 0
      return
    }

    const bed = this.bedSampler(this.position.x, this.position.z)

    // Vertical: a stiff spring-damper holds the keel on the bed rather than
    // letting weight drag it under. Scaled by mass so it balances gravity.
    const keel = this.position.y - this.config.draft
    const penetration = bed - keel
    if (penetration > 0) {
      const support = penetration * this.config.mass * 160 - this.velocity.y * this.config.mass * 22
      if (support > 0) this.force.y += support
    }

    // Grip comes from how little water there is to float in, NOT from how far
    // the keel is pressed into the sand — a log lying *on* the beach has no
    // penetration yet is thoroughly stuck. With less than a draft of water the
    // hull is bearing weight on the bottom; with a full draft or more it floats
    // free. (Mean sea level is ~0; the swell's few centimetres don't matter to
    // a grounded body.)
    const waterDepth = -bed
    const grip = Math.min(Math.max(1 - waterDepth / this.config.draft, 0), 1)
    this.grounding = grip
    if (grip <= 0) return

    // Horizontal Coulomb friction.
    const friction = grip * (this.config.groundFriction ?? 0)
    if (friction > 0) {
      const fx = this.force.x
      const fz = this.force.z
      const applied = Math.hypot(fx, fz)
      const speed = Math.hypot(this.velocity.x, this.velocity.z)

      if (speed < 0.06 && applied <= friction) {
        // Stiction: cancel the applied push and bleed any residual creep.
        this.force.x -= fx
        this.force.z -= fz
        this.velocity.x *= 0.4
        this.velocity.z *= 0.4
      } else {
        // Sliding: kinetic friction opposes motion (or the push, if stopped).
        const kinetic = friction * 0.7
        let dx: number
        let dz: number
        if (speed > 0.05) {
          dx = this.velocity.x / speed
          dz = this.velocity.z / speed
        } else {
          dx = fx / (applied || 1)
          dz = fz / (applied || 1)
        }
        this.force.x -= dx * kinetic
        this.force.z -= dz * kinetic
      }

      // Dragging over sand kills any spin.
      this.torqueYaw -= this.yawRate * grip * friction * 1.5
    }
  }

  private applyHydrodynamicDrag(): void {
    const cos = Math.cos(this.yaw)
    const sin = Math.sin(this.yaw)
    const vf = this.velocity.x * sin + this.velocity.z * cos
    const vl = this.velocity.x * cos - this.velocity.z * sin

    // Quadratic drag: the sign of the velocity term is preserved by using |v|*v.
    const df = -this.config.dragForward * Math.abs(vf) * vf
    const dl = -this.config.dragLateral * Math.abs(vl) * vl

    // Longitudinal drag acts through the centre of mass; lateral drag acts aft
    // of it, which supplies directional stability.
    this.force.x += dl * cos + df * sin
    this.force.z += -dl * sin + df * cos
    this.torqueYaw += -this.config.lateralResistanceOffset * dl

    this.torqueYaw += -this.config.dragYaw * Math.abs(this.yawRate) * this.yawRate
  }

  private applyBuoyancy(dt: number, elapsed: number): void {
    const { mass, draft } = this.config
    const weight = mass * GRAVITY
    const perSample = weight / this.samples.length / REST_SUBMERSION

    let verticalForce = 0
    let torquePitch = 0
    let torqueRoll = 0
    let submergedTotal = 0
    let waterVelocitySum = 0
    let waterPitchVelocity = 0
    let waterRollVelocity = 0

    for (const sample of this.samples) {
      _worldPoint.set(sample.x, 0, sample.z).applyQuaternion(this.quaternion).add(this.position)
      const water = sampleWater(_worldPoint.x, _worldPoint.z, elapsed)

      const depth = water.height - _worldPoint.y
      const submersion = Math.min(Math.max(depth / draft + REST_SUBMERSION, 0), 1)
      submergedTotal += submersion
      // A boat driving into a crest encounters a faster rise than a stationary
      // buoy. Include travel across the slope and the probe's yaw motion; using
      // only the wave clock actively damps the hull down into an oncoming wave.
      const vx = this.velocity.x + this.yawRate * (_worldPoint.z - this.position.z)
      const vz = this.velocity.z - this.yawRate * (_worldPoint.x - this.position.x)
      const waterVelocity = water.verticalVelocity - (water.normalX * vx + water.normalZ * vz) / Math.max(water.normalY, .05)
      waterVelocitySum += waterVelocity
      waterPitchVelocity -= sample.z * waterVelocity
      waterRollVelocity += sample.x * waterVelocity

      // Keep the support spring active when a steep crest reaches the hull.
      // Capping it at full displacement made every flooded probe exert the
      // same force, removing both the extra lift and the bow-up torque.
      const lift = perSample * Math.max(depth / draft + REST_SUBMERSION, 0)
      verticalForce += lift

      // Levers taken in the body frame. Positive lift forward pitches the bow
      // up, which under YXZ is a negative rotation about X.
      torquePitch += -sample.z * lift
      torqueRoll += sample.x * lift
    }

    this.submersion = submergedTotal / this.samples.length

    // Weight, and damping against the water's own vertical motion so the hull
    // settles instead of pogoing on every crest.
    verticalForce -= weight
    const heaveDamping = 2 * 0.9 * mass * this.naturalFrequency
    verticalForce -= heaveDamping * (this.velocity.y - waterVelocitySum / this.samples.length)
    this.force.y += verticalForce

    // Dampen motion relative to the changing slope under the entire hull.
    // Damping toward zero pitch/roll rate would hold the bow flat as a crest
    // arrives. The footprint average lets long ships bridge the shorter chop.
    const pitchDamping = (this.config.pitchDamping ?? 0.85) * 2 * this.inertiaPitch * this.naturalFrequency
    const rollDamping = (this.config.rollDamping ?? 0.7) * 2 * this.inertiaRoll * this.naturalFrequency
    // A fast RIB can cross several short crests a second. Limit the angular
    // feed-forward so that chop cannot snap the boat from one tilt limit to
    // the other; heave still follows the full encountered water motion.
    const waterPitchRate = Math.max(-.8, Math.min(.8, waterPitchVelocity / Math.max(this.samplePitchMoment, 1)))
    const waterRollRate = Math.max(-.7, Math.min(.7, waterRollVelocity / Math.max(this.sampleRollMoment, 1)))
    torquePitch -= pitchDamping * (this.pitchRate - waterPitchRate)
    torqueRoll -= rollDamping * (this.rollRate - waterRollRate)

    // Add turn heel to the floating attitude instead of easing the hull back
    // toward level after every step, which used to erase its wave-driven roll.
    const heel = Math.max(-.2, Math.min(.2, -this.yawRate * this.forwardSpeed * .05))
    torqueRoll += (perSample / draft) * this.sampleRollMoment * heel

    this.pitchRate += (torquePitch / this.inertiaPitch) * dt
    this.rollRate += (torqueRoll / this.inertiaRoll) * dt
  }

  private integrate(dt: number): void {
    const invMass = 1 / this.config.mass

    this.velocity.x += this.force.x * invMass * dt
    this.velocity.y += this.force.y * invMass * dt
    this.velocity.z += this.force.z * invMass * dt

    this.position.x += this.velocity.x * dt
    this.position.y += this.velocity.y * dt
    this.position.z += this.velocity.z * dt

    this.yawRate += (this.torqueYaw / this.inertiaYaw) * dt
    this.yaw += this.yawRate * dt

    this.pitch += this.pitchRate * dt
    this.roll += this.rollRate * dt

    // A hull that reaches these angles has already lost; clamping keeps the
    // simulation from exploding while the player recovers.
    const limit = 0.55
    if (Math.abs(this.pitch) > limit) {
      this.pitch = Math.sign(this.pitch) * limit
      this.pitchRate = 0
    }
    if (Math.abs(this.roll) > limit) {
      this.roll = Math.sign(this.roll) * limit
      this.rollRate = 0
    }

    // Numerical safety net. A violent tow snap on a light body (a log yanked
    // by a fully-loaded wire) can spike a rate for a step; a single non-finite
    // value reaching the quaternion silently poisons everything that reads the
    // transform afterwards — tension goes to NaN, the tow quietly dies. Reset
    // anything non-finite and cap rates to well past any real value.
    if (!Number.isFinite(this.yawRate)) this.yawRate = 0
    if (!Number.isFinite(this.yaw)) this.yaw = 0
    if (!Number.isFinite(this.rollRate)) this.rollRate = 0
    if (!Number.isFinite(this.roll)) this.roll = 0
    if (!Number.isFinite(this.pitchRate)) this.pitchRate = 0
    if (!Number.isFinite(this.pitch)) this.pitch = 0
    this.yawRate = Math.max(-3, Math.min(3, this.yawRate))
    const speedSq = this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z
    const maxSpeed = this.config.maxSpeed ?? 40
    if (!Number.isFinite(speedSq)) {
      this.velocity.set(0, this.velocity.y, 0)
    } else if (speedSq > maxSpeed * maxSpeed) {
      const s = maxSpeed / Math.sqrt(speedSq)
      this.velocity.x *= s
      this.velocity.z *= s
    }

    this.updateQuaternion()
  }

  // -- collision ------------------------------------------------------------

  /**
   * Hull-against-hull contact, e.g. the tug nosing its own barge.
   *
   * Both hulls are rows of circles; every overlapping pair contributes a
   * contact. Positional correction and the normal impulse are split by mass, so
   * a 280 t tug shoulders a 1,400 t barge barely a fifth as far as it is
   * shoved back itself. Restitution is low: steel-on-fendered-steel thuds, it
   * does not bounce.
   */
  collideWithVessel(other: Vessel, restitution = 0.15): void {
    const invMassA = 1 / this.config.mass
    const invMassB = 1 / other.config.mass
    const invMassSum = invMassA + invMassB

    for (const circleA of this.hullCircles) {
      _worldPoint.set(0, 0, circleA.z).applyQuaternion(this.quaternion).add(this.position)

      for (const circleB of other.hullCircles) {
        _contact.set(0, 0, circleB.z).applyQuaternion(other.quaternion).add(other.position)

        let nx = _worldPoint.x - _contact.x
        let nz = _worldPoint.z - _contact.z
        const distance = Math.hypot(nx, nz)
        const minDistance = circleA.r + circleB.r
        if (distance >= minDistance || distance < 1e-5) continue

        nx /= distance
        nz /= distance
        const penetration = minDistance - distance

        // Separate the hulls, heavier one moving less.
        this.position.x += nx * penetration * (invMassA / invMassSum)
        this.position.z += nz * penetration * (invMassA / invMassSum)
        other.position.x -= nx * penetration * (invMassB / invMassSum)
        other.position.z -= nz * penetration * (invMassB / invMassSum)

        // Relative velocity along the normal, positive when closing.
        const closing =
          (other.velocity.x - this.velocity.x) * nx + (other.velocity.z - this.velocity.z) * nz
        if (closing <= 0) continue

        const impulse = (closing * (1 + restitution)) / invMassSum
        this.velocity.x += nx * impulse * invMassA
        this.velocity.z += nz * impulse * invMassA
        other.velocity.x -= nx * impulse * invMassB
        other.velocity.z -= nz * impulse * invMassB

        // Yaw kick on both hulls from the off-centre contact.
        const cosA = Math.cos(this.yaw)
        const sinA = Math.sin(this.yaw)
        this.yawRate += (circleA.z * (nx * cosA - nz * sinA) * impulse) / this.inertiaYaw
        const cosB = Math.cos(other.yaw)
        const sinB = Math.sin(other.yaw)
        other.yawRate -= (circleB.z * (nx * cosB - nz * sinB) * impulse) / other.inertiaYaw

        if (closing > 0.4) {
          const impactSpeed = closing
          this.damage = Math.min(1, this.damage + impactSpeed * 0.015)
          other.damage = Math.min(1, other.damage + impactSpeed * 0.015)
          this.collisions.push({ impactSpeed, point: _contact.clone() })
        }
      }
    }
  }

  /**
   * Push the hull out of any quay or breakwater it has driven into.
   *
   * The hull is approximated by a row of circles along the centreline, which is
   * accurate enough for a boat and far cheaper than a real convex test. Each
   * contact corrects position, kills the inbound component of velocity and
   * applies a yaw impulse from its lever arm, so clipping a dock corner spins
   * you the way it should.
   */
  resolveCollisions(obstacles: readonly Obstacle[], restitution = 0.25): void {
    for (const circle of this.hullCircles) {
      _worldPoint.set(0, 0, circle.z).applyQuaternion(this.quaternion).add(this.position)

      for (const obstacle of obstacles) {
        closestPointOnObstacle(obstacle, _worldPoint.x, _worldPoint.z, _contact)

        let nx = _worldPoint.x - _contact.x
        let nz = _worldPoint.z - _contact.z
        const distance = Math.hypot(nx, nz)
        if (distance >= circle.r) continue

        if (distance < 1e-5) {
          // Dead centre: push along the obstacle's short axis rather than a
          // random direction.
          nx = Math.cos(obstacle.rotation)
          nz = -Math.sin(obstacle.rotation)
        } else {
          nx /= distance
          nz /= distance
        }

        const penetration = circle.r - distance
        this.position.x += nx * penetration
        this.position.z += nz * penetration

        const closing = this.velocity.x * nx + this.velocity.z * nz
        if (closing < 0) {
          const impulse = -closing * (1 + restitution)
          this.velocity.x += nx * impulse
          this.velocity.z += nz * impulse

          // Lever arm about the vertical axis, taken in the body frame. A hit
          // forward of amidships yaws the opposite way to one aft of it.
          const cos = Math.cos(this.yaw)
          const sin = Math.sin(this.yaw)
          const fx = nx * cos - nz * sin
          this.yawRate += (circle.z * fx * impulse * this.config.mass) / this.inertiaYaw

          const impactSpeed = -closing
          if (impactSpeed > 0.4) {
            this.damage = Math.min(1, this.damage + impactSpeed * 0.02)
            this.collisions.push({ impactSpeed, point: _contact.clone() })
          }
        }
      }
    }
  }
}
